import { useMemo, useEffect, useCallback, useState } from "react";
import { MessageList, TypingIndicator } from "@chatscope/chat-ui-kit-react";
import {
  useChat,
  MessageContentType,
  MessageDirection,
  MessageStatus,
  MessageContent,
  TextContent,
} from "@chatscope/use-chat";

import ChatListContainer from "./ChatListContainer";
import MessageBoxHead from "./MessagesBoxHead";
import { useTranslation } from "react-i18next";
import ChatBoxContainer from "./ChatBoxContainer";
import MessageInput from "./MessageInput";
import { useTwilioChat } from "../../hooks/useTwilioChat";
import {
  AuthContextInterface,
  UserType,
  useAuth,
} from "../../context/AuthContext";
import EmptyChatPage from "../contact/EmptyChatsPage";
import { Conversation, Media, Message } from "@twilio/conversations";
import { classNames, showApiErrorMessages } from "../../helpers";
import ChatListPage from "../../views/candidate/dashboard/ChatListPage";
import ContactsWrapper from "./ContactsWrapper";
import { useLocation, useParams } from "react-router-dom";
import { getLastMessage } from "../../api/endpoints/chat/conversations";
import ConversationParticipantModel from "../../api/models/ConversationParticipantModel";
type ChatContainerProps = {
  user: any;
  updateConversation: (conversation: Conversation | any) => void;
  refetchConversations: () => void;
};

export default function MainChatContainer(props: ChatContainerProps) {
  const { user, updateConversation, refetchConversations } = props;
  const LocalBasePath = "page.chat";
  const { t } = useTranslation("", { keyPrefix: LocalBasePath });
  const [typing, setTyping] = useState<boolean>(false);
  const [showContactList, setShowContactList] = useState<boolean>(false);
  const [showChatList, setShowChatList] = useState<boolean>(true);
  const { state: stateOfId }: any = useLocation();

  const { currentUser: userInfo }: AuthContextInterface = useAuth()!;

  const {
    getMessages,
    messagesList,
    setConversationMessagesToReadStatus,
    getActiveConversation,
    scrollChatCardIntoView,
  } = useTwilioChat();

  const userData: AuthContextInterface = useAuth()!;

  const {
    conversations,
    activeConversation,
    setActiveConversation,
    getUser,
    messages,
    currentMessage,
    setCurrentMessage,
    sendTyping,
    addMessage,
    removeMessagesFromConversation,
    getConversation,
    setCurrentUser,
    currentMessages,
    currentUser,
    service,
  } = useChat();

  const getActiveConversations = useCallback(
    (sid) => {
      return getActiveConversation(sid);
    },
    [activeConversation?.id]
  );

  // Get current user data
  const receiverParticipant: ConversationParticipantModel = useMemo(() => {
    if (activeConversation) {
      return activeConversation.data.getReceiverParticipant(
        userData.currentUser!.twilioIdentity!
      );
    }
  }, [activeConversation, getUser]);

  const updateLastMessage = async () => {
    const response = await getLastMessage({
      conversation_id: activeConversation!.data.id,
    });
    if (!response?.isSuccess) {
      showApiErrorMessages(response!);
    }
  };

  const handleSend = async (text: string | File, type?: string) => {
    if ((!!currentMessage || !!type) && !!text) {
      let messageContent: string | File = text;
      if (!!type) {
        const fileToDataUri = (file: File) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (event: any) => {
              resolve(event.target.result);
            };
            reader.readAsDataURL(file);
          });
        await fileToDataUri(text as File).then((dataUri: any) => {
          messageContent = `${dataUri}`;
        });
      }
      const message = {
        id: "", // Id will be generated by storage generator, so here you can pass an empty string
        content: messageContent as unknown as MessageContent<TextContent>,
        contentType: !!type
          ? MessageContentType.Attachment
          : MessageContentType.TextHtml,
        senderId: user.id,
        direction: MessageDirection.Outgoing,
        status: MessageStatus.Sent,
        sentTime: new Date(),
        createdTime: new Date(),
        type: type,
      };
      if (activeConversation) {
        addMessage(message, activeConversation?.id ?? "", true);
        updateLastMessage();
        setCurrentMessage("");
      }
    }
  };

  useEffect(() => {
    if (!!user) setCurrentUser(user);
  }, [user, setCurrentUser]);

  const handleMessageList = useCallback(async () => {
    const activeConversationId = activeConversation?.id;
    if (activeConversationId) {
      removeMessagesFromConversation(activeConversationId);
      setConversationMessagesToReadStatus(activeConversationId);
      try {
        const items = await getMessages(activeConversationId);
        if (items.length > 0) {
          for (const messageData of items) {
            const body = !!messageData?.attributes?.type
              ? await getImageUrl(messageData)
              : messageData?.body;

            const message = {
              id: "",
              content: body as unknown as MessageContent<TextContent>,
              contentType: !!messageData?.attributes?.type
                ? MessageContentType.Attachment
                : MessageContentType.TextHtml,
              senderId: messageData?.author ?? "",
              direction:
                messageData?.attributes?.author_email ===
                userData?.currentUser?.account?.email
                  ? MessageDirection.Outgoing
                  : MessageDirection.Incoming,
              status:
                messageData?.attributes?.author_email ==
                userData?.currentUser?.account?.email
                  ? MessageStatus.Sent
                  : MessageStatus.DeliveredToDevice,
              sentTime: messageData?.dateCreated,
              createdTime: messageData?.dateCreated,
            };
            await addMessage(message, activeConversation?.id ?? "", true);
          }
        }
      } catch (error) {
        console.error(error);
      }
    }
  }, [activeConversation?.id, messagesList, activeConversation]);

  const getImageUrl = async (messageData: Message) => {
    if (!messageData) return;
    const categorizedMedia: Media[] | null =
      await messageData.getMediaByCategory(["media"]);

    if (!!categorizedMedia) {
      const imageUrl = await categorizedMedia[0].getContentTemporaryUrl();
      return imageUrl;
    }
  };

  const setNewMessage = useCallback(
    async (messageData: any) => {
      const idConversation = messageData?.conversation?.sid;
      const conversation = getConversation(idConversation);
      if (
        messageData?.attributes?.author_email !==
        userData?.currentUser?.account?.email
      ) {
        const body = !!messageData?.attributes?.type
          ? await getImageUrl(messageData)
          : messageData?.body;
        const message = {
          id: "", // Id will be generated by storage generator, so here you can pass an empty string
          content: body as unknown as MessageContent<TextContent>,
          contentType: !!messageData?.attributes?.type
            ? MessageContentType.Attachment
            : MessageContentType.TextHtml,
          senderId: messageData?.author ?? "",
          direction: MessageDirection.Incoming,
          status: MessageStatus.DeliveredToDevice,
          sentTime: messageData?.dateCreated,
          createdTime: messageData?.dateCreated,
        };
        addMessage(message, idConversation, true);
        if (conversation) {
          conversation.unreadCounter++;
          updateConversation(conversation);
        }
      }
    },
    [activeConversation, conversations, updateConversation]
  );

  const handleChange = (value: string) => {
    // Send typing indicator to the active conversation
    // You can call this method on each onChange event
    // because sendTyping method can throttle sending this event
    // So typing event will not be send to often to the server
    getActiveConversations(activeConversation?.id).then((conversation) => {
      conversation?.typing();
    });

    setCurrentMessage(value);
    if (activeConversation) {
      sendTyping({
        conversationId: activeConversation?.id,
        isTyping: true,
        userId: user.id,
        content: value,
        throttle: true,
      });
    }
  };

  useEffect(() => {
    if (activeConversation?.id) {
      handleMessageList();
      getActiveConversations(activeConversation?.id).then((conversation) => {
        conversation?.on("typingStarted", function (participant) {
          //process the participant to show typing
          setTyping(true);
        });
        conversation?.on("typingEnded", function (participant) {
          //process the participant to show typing
          setTyping(false);
        });
        conversation?.on("messageAdded", (newMessage) => {
          setNewMessage(newMessage);
        });
      });
    }
  }, [activeConversation?.id]);

  useEffect(() => {
    if (!!stateOfId?.id) {
      _setActiveConversation(stateOfId?.id);
    } else if (conversations?.length) {
      _setActiveConversation(conversations[0]?.id);
    }
  }, [currentUser, stateOfId]);

  const _setActiveConversation = (twilioId: string) => {
    setActiveConversation(twilioId);
    scrollChatCardIntoView(twilioId);
    setConversationMessagesToReadStatus(twilioId);
  };

  if (conversations.length === 0) {
    return (
      <EmptyChatPage
        setShowContactList={() => {
          setShowContactList(true);
          setShowChatList(false);
        }}
        showContactList={showContactList}
      />
    );
  }

  return (
    <div className="w-full  h-full   md:px-6 md:py-4">
      <div className="w-full relative flex h-full flex-col  md:rounded-md md:shadow  md:overflow-hidden md:flex-row   bg-white">
        <aside className=" bg-white sticky top-0 flex-shrink-0 h-full  hidden md:flex md:flex-col md:w-72 ltr:border-r rtl:border-l border-gray-200 overflow-hidden">
          <ChatListContainer
            setActiveConversation={_setActiveConversation}
            activeConversation={activeConversation}
            userList={conversations}
            getUser={getUser}
            refetchConversations={refetchConversations}
            setShowContactList={() => setShowContactList(!showContactList)}
          />
        </aside>
        {showChatList && !showContactList && (
          <div className={"flex flex-col h-full  md:hidden"}>
            <ChatListPage
              setActiveConversation={_setActiveConversation}
              activeConversation={activeConversation}
              conversations={conversations}
              setShowChatList={() => {
                setShowChatList(!showChatList);
                setShowContactList(false);
              }}
              setShowContactList={() => {
                setShowContactList(!showContactList);
                setShowChatList(false);
              }}
              getUser={getUser}
            />
          </div>
        )}
        {showContactList && !showChatList && (
          <div className="flex flex-col h-full  md:hidden">
            <ContactsWrapper refetchConversations={refetchConversations} />
          </div>
        )}
        <div
          className={classNames(
            "h-full  relative  bg-white  flex-1 ",
            !showChatList && !showContactList
              ? "flex flex-col"
              : "hidden md:flex md:flex-col"
          )}
        >
          <div className="w-full">
            <MessageBoxHead
              senderName={receiverParticipant.displayName}
              senderType={UserType.Candidate}
              senderImage={receiverParticipant.profilePictureUrl}
              setShowChatList={() => setShowChatList(!showChatList)}
              participantId={receiverParticipant?.id}
            />
          </div>
          <div className="w-full relative box-border  h-[320px]     flex flex-col flex-grow ">
            <MessageList
              className="w-full  md:px-8   px-4 overflow-y-auto   relative  h-full"
              autoScrollToBottom={true}
              typingIndicator={
                <>
                  {typing && (
                    <TypingIndicator
                      className="animate-pulse  text-gray-500 text-sm font-medium mb-2 flex justify-start"
                      content={t("userWriting", {
                        name: receiverParticipant.displayName,
                      })}
                    />
                  )}
                </>
              }
            >
              <ChatBoxContainer
                showChatList={showChatList}
                messagesList={currentMessages ?? []}
              />
            </MessageList>
          </div>
          <div className="w-full  md:px-8 md:pb-4">
            <MessageInput
              value={currentMessage}
              handleChange={handleChange}
              onSend={handleSend}
              userType={userInfo!.object_type!.toLowerCase()}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
